import { ReactNode, useEffect, useRef, useState } from 'react';
import { Box, IconButton, InputAdornment, Link, TextField, Tooltip, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import { Breadcrumb } from 'components';
import { SupportImage } from './SupportImage';

interface InstructionItem {
    type?: string;
    description?: string;
    step?: string;
    number?: number;
    url?: string;
    image?: string;
    alt?: string;
    subpoints?: string[];
    subpointListType?: string;
}

interface InstructionSection {
    title: string;
    image?: string;
    items?: InstructionItem[];
    steps?: InstructionItem[];
}

interface InstructionGroup {
    items: InstructionItem[];
    image?: string;
    alt?: string;
}

interface WorkInstructionsProps {
    title: string;
    nav: { name: string; path: string }[];
    sections: InstructionSection[];
    children?: ReactNode;
}

const groupInstructions = (section: InstructionSection) => {
    const groups: InstructionGroup[] = [];
    let pending: InstructionItem[] = [];

    (section.steps ?? section.items ?? []).forEach((item) => {
        if (item.type !== 'image') pending.push(item);
        if (item.image) {
            groups.push({ items: pending, image: item.image, alt: item.alt });
            pending = [];
        }
    });

    if (pending.length || section.image) {
        groups.push({ items: pending, image: section.image, alt: section.title });
    }
    return groups;
};

const scrollToInstruction = (container: HTMLDivElement | null, navigation: HTMLElement | null, section: HTMLElement | null | undefined) => {
    if (!container || !section) return;
    const sectionBounds = section.getBoundingClientRect();
    const navigationBounds = navigation?.getBoundingClientRect();
    const topOffset = navigationBounds && navigationBounds.right > sectionBounds.left ? navigationBounds.height + 16 : 16;
    container.scrollTo({ top: Math.max(0, sectionBounds.top - container.getBoundingClientRect().top + container.scrollTop - topOffset), behavior: 'smooth' });
};

export const WorkInstructions = ({ title, nav, sections, children }: WorkInstructionsProps) => {
    const [query, setQuery] = useState('');
    const [matchIndex, setMatchIndex] = useState(0);
    const sectionRefs = useRef<Array<HTMLElement | null>>([]);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const navigationRef = useRef<HTMLElement | null>(null);
    const searchTerms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matchingSections = searchTerms.length ? sections.reduce<number[]>((matches, section, index) => {
        const content = [section.title, ...(section.steps ?? section.items ?? []).flatMap((item) => [item.description, item.step, item.alt, item.url, ...(item.subpoints ?? [])])].join(' ').toLowerCase();
        if (searchTerms.every((term) => content.includes(term))) matches.push(index);
        return matches;
    }, []) : [];
    const selectedSection = matchingSections[matchIndex];

    useEffect(() => {
        if (selectedSection === undefined) return;
        const timer = setTimeout(() => {
            scrollToInstruction(scrollContainerRef.current, navigationRef.current, sectionRefs.current[selectedSection]);
        }, 250);
        return () => clearTimeout(timer);
    }, [query, selectedSection]);

    const moveMatch = (direction: number) => {
        if (matchingSections.length) setMatchIndex((current) => (current + direction + matchingSections.length) % matchingSections.length);
    };

    return (
    <Box sx={{ px: { xs: 2, md: 4 }, py: 3, minHeight: 600, maxWidth: 1440, mx: 'auto', color: '#243746' }}>
        <Box sx={{ mb: 3, overflowWrap: 'anywhere' }}><Breadcrumb navList={nav} /></Box>
        <Box component="header" sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 1, pl: 2, py: 0.5, mb: 3, borderLeft: '3px solid #f58220' }}>
            <Typography component="h1" sx={{ m: 0, minWidth: 0, fontSize: 22, fontWeight: 700, color: '#003b7a', lineHeight: 1.4, overflowWrap: 'anywhere' }}>{title}</Typography>
            <TextField
                label="Search instructions"
                value={query}
                size="small"
                onChange={(event) => { setQuery(event.target.value); setMatchIndex(0); }}
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); moveMatch(event.shiftKey ? -1 : 1); } }}
                sx={{ width: { xs: '100%', sm: 340 }, maxWidth: '100%', flexShrink: 0 }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    endAdornment: query ? <InputAdornment position="end">
                        <Typography role="status" aria-live="polite" sx={{ whiteSpace: 'nowrap', fontSize: 11, minWidth: 28, textAlign: 'center' }}>{matchingSections.length ? `${matchIndex + 1}/${matchingSections.length}` : 'No matches'}</Typography>
                        <Tooltip title="Previous matching section"><span><IconButton size="small" aria-label="Previous matching section" disabled={matchingSections.length < 2} onClick={() => moveMatch(-1)}><KeyboardArrowUpIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Next matching section"><span><IconButton size="small" aria-label="Next matching section" disabled={matchingSections.length < 2} onClick={() => moveMatch(1)}><KeyboardArrowDownIcon fontSize="small" /></IconButton></span></Tooltip>
                        <Tooltip title="Clear search"><IconButton size="small" aria-label="Clear search" onClick={() => { setQuery(''); setMatchIndex(0); }}><CloseIcon fontSize="small" /></IconButton></Tooltip>
                    </InputAdornment> : undefined,
                }}
            />
        </Box>
        <Box ref={scrollContainerRef} role="region" aria-label={`${title} instructions`} tabIndex={0} sx={{ display: { xs: 'block', lg: 'grid' }, gridTemplateColumns: { lg: '200px minmax(0, 1fr)' }, gap: { lg: 5 }, alignItems: 'start', position: 'relative', maxHeight: 'calc(100dvh - 260px)', minHeight: 320, overflowY: 'auto', overscrollBehavior: 'contain', pr: 1 }}>
            <Box component="nav" ref={navigationRef} aria-label={`${title} sections`} sx={{ position: 'sticky', top: 0, zIndex: 1, alignSelf: 'start', bgcolor: '#fff', pb: 1, mb: { xs: 3, lg: 0 }, maxHeight: { xs: 144, lg: 'max(144px, calc(100dvh - 280px))' }, overflowY: 'auto' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#647482', mb: 1 }}>ON THIS PAGE</Typography>
                <Box component="ol" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: { xs: 'row', lg: 'column' }, flexWrap: 'wrap', gap: { xs: 0.5, lg: 0 } }}>
                    {sections.map((section, index) => (
                        <Box component="li" key={section.title}>
                            <Link href={`#instruction-${index + 1}`} onClick={(event) => { event.preventDefault(); scrollToInstruction(scrollContainerRef.current, navigationRef.current, sectionRefs.current[index]); }} aria-current={selectedSection === index ? 'location' : undefined} underline="hover" sx={{ display: 'block', px: 1.25, py: 0.75, borderLeft: '2px solid #dce3e8', color: '#42586a', fontSize: 12, lineHeight: 1.5, '&[aria-current]': { borderColor: '#f58220', color: '#003b7a', bgcolor: '#edf3f8', fontWeight: 700 }, '&:hover': { borderColor: '#f58220', color: '#003b7a', bgcolor: '#f4f7f9' } }}>
                                {index + 1}. {section.title}
                            </Link>
                        </Box>
                    ))}
                </Box>
            </Box>
            <Box component="article" sx={{ minWidth: 0 }}>
                {children}
                {sections.map((section, sectionIndex) => (
                    <Box component="section" ref={(element: HTMLElement | null) => { sectionRefs.current[sectionIndex] = element; }} id={`instruction-${sectionIndex + 1}`} aria-labelledby={`instruction-title-${sectionIndex + 1}`} key={section.title} sx={{ pb: 4, '& + section': { pt: 3, borderTop: '1px solid #dce3e8' }, ...(selectedSection === sectionIndex && { '& h2': { textDecoration: 'underline', textDecorationColor: '#f58220', textUnderlineOffset: '6px' } }) }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                            <Typography component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', bgcolor: '#edf3f8', color: '#003b7a', fontFamily: (theme) => theme.typography.fontFamily, fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: 0, lineHeight: 1, flexShrink: 0 }}>{String(sectionIndex + 1).padStart(2, '0')}</Typography>
                            <Typography component="h2" id={`instruction-title-${sectionIndex + 1}`} sx={{ color: '#003b7a', fontSize: 16, lineHeight: 1.4, fontWeight: 700 }}>{section.title}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {groupInstructions(section).map((group, groupIndex) => (
                                <Box key={groupIndex} sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: group.image ? 'minmax(0, 0.85fr) minmax(0, 1.35fr)' : 'minmax(0, 1fr)' }, gap: { xs: 1.5, md: 3 }, alignItems: 'start' }}>
                                    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {group.items.map((item, itemIndex) => (
                                            <Box key={itemIndex} sx={{
                                                ...(item.type === 'note' ? { borderLeft: '3px solid #e6a14e', bgcolor: '#fff8ed', px: 1.5, py: 1.25 } : {}),
                                                ...(item.type === 'sub-bullet' ? { pl: 2 } : {}),
                                            }}>
                                                <Typography component="p" sx={{ m: 0, fontSize: 12, lineHeight: 1.75, overflowWrap: 'anywhere' }}>
                                                    {item.type === 'note' && <Box component="span" sx={{ fontWeight: 700 }}>Note: </Box>}
                                                    {(item.step || item.number !== undefined) && <Box component="span" sx={{ color: '#003b7a', fontWeight: 700, mr: 0.75 }}>{item.step ? `${item.step}:` : `${item.number}.`}</Box>}
                                                    {item.description}
                                                    {item.url && <> via <Link href={item.url} target="_blank" rel="noopener noreferrer">{item.url}</Link></>}
                                                </Typography>
                                                {item.subpoints && <Box component={item.subpointListType === 'ordered' ? 'ol' : 'ul'} sx={{ my: 1, pl: 2.5, fontSize: 12, lineHeight: 1.75, '& li + li': { mt: 0.5 } }}>{item.subpoints.map((subpoint) => <li key={subpoint}>{subpoint}</li>)}</Box>}
                                            </Box>
                                        ))}
                                    </Box>
                                    {group.image && <Box sx={{ minWidth: 0, '& figure': { my: 0 } }}><SupportImage image={group.image} alt={group.alt ?? section.title} /></Box>}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    </Box>
    );
};